import { PathLocationStrategy } from '@angular/common';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

/**
 * Co-brands: partner companies that run this marketplace with their own colors, logo and name.
 * Only the look changes; users, assets and loans are the same as on RealWorld.fi.
 *
 * The brand comes from its number in the URL, on any page, and is loaded in main.ts BEFORE
 * Angular boots: https://app.realworld.fi/all-assets?community_id=1. The URL is the only place the
 * number lives (nothing is stored in the browser): CommunityLocationStrategy keeps ?community_id=
 * in the address bar on every page the app opens, and a URL without it is the normal RealWorld site.
 * On a page opened with ?community_id=, a signed-in user gets the community GET /auth/me gives
 * them (the address bar switches to its number), or the RealWorld site when it gives null. The same
 * rule runs again right after a sign-in on that page (followSignedInCommunity). A visitor who is not
 * signed in sees the community in the URL, so the sign-up link (/sign-up?community_id=1) shows its look.
 * The brand's colors are written to the --rw-* CSS variables defined in src/styles.css,
 * whose defaults are RealWorld's own, so with no brand nothing changes.
 * Brands are managed in the admin panel (Community Management).
 */

export interface ICoBrandTheme {
    primary: string;
    secondary: string;
    accent: string;
    page_bg: string;
    auth_bg: string;
    /** Main text color (titles, tabs, names, amounts); defaults to RealWorld's navy. */
    text?: string;
    /** Top bar background; when missing the header follows the primary color. */
    header_bg?: string;
    /** Bottom bar background; its text turns white when it is dark. */
    footer_bg?: string;
}

export interface ICoBrand {
    name: string;
    /** The community's number (1, 2, 3...) used in ?community_id=; null for an unsaved one in the admin preview. */
    community_id: number | null;
    /** 'disabled' brands get the "temporarily unavailable" page instead of the marketplace. */
    status?: 'active' | 'disabled';
    theme: ICoBrandTheme;
    logo_url: string;
    favicon_url: string;
    footer_text: string;
    /** The community's own privacy policy (PDF); empty when it has none. */
    privacy_policy_url?: string;
    /**
     * Locations (vaults) locked to the community, by name, e.g. ['Miami']. Loan requests and sale
     * listings on items held there are members only. Empty or missing: no location is locked.
     */
    locations?: string[];
}

export interface ICoBrandBoot {
    brand: ICoBrand | null;
    /**
     * Set when the marketplace must not start for this community: 'disabled' (brand switched off
     * in the admin panel) or 'offline' (brand API unreachable and nothing cached). main.ts then shows
     * the "temporarily unavailable" page instead of "page not found".
     */
    unavailable?: 'disabled' | 'offline';
}

const FETCH_TIMEOUT_MS = 4000;
const COMMUNITY_PARAM = 'community_id';
/** Keys older builds stored the community in; removed at startup, the URL is the only source now. */
const OLD_STORAGE_KEYS = ['rw.coBrand', 'rw.communityId'];
const COMMUNITY_ID_PATTERN = /^[1-9]\d{0,8}$/;

/** Theme variable -> brand theme field. Shades not listed here are derived in brandVariables(). */
const BASE_VARIABLES: Record<string, Exclude<keyof ICoBrandTheme, 'text' | 'header_bg' | 'footer_bg'>> = {
    '--rw-primary': 'primary',
    '--rw-secondary': 'secondary',
    '--rw-accent': 'accent',
    '--rw-page-bg': 'page_bg',
    '--rw-auth-bg': 'auth_bg',
};

/** Every --rw-* variable a brand sets (defaults live in src/styles.css :root). */
const THEME_VARIABLE_NAMES = [
    ...Object.keys(BASE_VARIABLES),
    '--rw-text', '--rw-header-bg', '--rw-header-btn', '--rw-footer-bg', '--rw-footer-text', '--rw-footer-link', '--rw-auth-text', '--rw-auth-muted', '--rw-auth-muted-2', '--rw-accent-mid', '--rw-accent-light', '--rw-auth-surface', '--rw-auth-input',
    '--rw-auth-deep', '--rw-auth-raised', '--rw-auth-raised-2', '--rw-auth-line',
    '--rw-primary-soft', '--rw-primary-soft-2', '--rw-primary-deep', '--rw-header-btn-border',
    '--rw-register-from', '--rw-register-to', '--rw-surface', '--rw-surface-hover',
    '--rw-surface-alt', '--rw-surface-head', '--rw-surface-soft', '--rw-row-alt', '--rw-row-hover', '--rw-skeleton', '--rw-skeleton-shine',
    '--rw-skeleton-cool', '--rw-skeleton-cool-shine', '--rw-skeleton-block', '--rw-tab',
    '--rw-tab-hover', '--rw-tab-soft', '--rw-tab-strong', '--rw-tab-line',
];

let activeBrand: ICoBrand | null = null;
/** Number of the community this page shows (from the URL at startup); null on RealWorld. */
let activeCommunityId: number | null = null;
/** Request header that carries the community number to the API. */
export const COMMUNITY_HEADER = 'Community-Id';
/** Whether this page was opened with ?community_id=; only then does a sign-in pick the view. */
let openedWithCommunity = false;
/**
 * Whether the signed-in user is a member of the community this page shows. Only then is the
 * Community-Id header sent: the API refuses it (401) from anyone who is not signed in.
 */
let signedInMember = false;
/** Keys of an NFT's location attribute, the same three spellings the backend reads. */
const LOCATION_KEYS = ['Location', 'location', 'LOCATION'];
/** Favicon hrefs before any brand replaced them, restored when the RealWorld look comes back. */
const originalFavicons = new Map<HTMLLinkElement, string>();

/**
 * Mixes a #RRGGBB color toward white.
 * @param {string} hex base color
 * @param {number} amount 0 = unchanged, 1 = white
 * @returns {string} mixed #RRGGBB color
 */
function tint(hex: string, amount: number): string {
    const channels = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
    return '#' + channels
        .map((c) => Math.round(c + (255 - c) * amount).toString(16).padStart(2, '0'))
        .join('')
        .toUpperCase();
}

/**
 * Mixes a #RRGGBB color toward black.
 * @param {string} hex base color
 * @param {number} amount 0 = unchanged, 1 = black
 * @returns {string} mixed #RRGGBB color
 */
function shade(hex: string, amount: number): string {
    const channels = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
    return '#' + channels
        .map((c) => Math.round(c * (1 - amount)).toString(16).padStart(2, '0'))
        .join('')
        .toUpperCase();
}

/**
 * Mixes two #RRGGBB colors.
 * @param {string} from start color
 * @param {string} to end color
 * @param {number} amount 0 = from, 1 = to
 * @returns {string} mixed #RRGGBB color
 */
function blend(from: string, to: string, amount: number): string {
    const a = [1, 3, 5].map((i) => parseInt(from.slice(i, i + 2), 16));
    const b = [1, 3, 5].map((i) => parseInt(to.slice(i, i + 2), 16));
    return '#' + a.map((c, i) => Math.round(c + (b[i] - c) * amount).toString(16).padStart(2, '0')).join('').toUpperCase();
}

/**
 * Relative luminance of a #RRGGBB color (WCAG 2).
 * @param {string} hex color
 * @returns {number} 0 (black) to 1 (white)
 */
function luminance(hex: string): number {
    const [r, g, b] = [1, 3, 5].map((i) => {
        const c = parseInt(hex.slice(i, i + 2), 16) / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Text color for the log-in / register pages, picked from the sign-in background: white on a
 * dark background (RealWorld's own look), dark on a light one, whichever reads better.
 * @param {ICoBrandTheme} theme brand colors
 * @returns {string} #RRGGBB color
 */
function authTextColor(theme: ICoBrandTheme): string {
    const bg = luminance(theme.auth_bg);
    const onWhite = (1 + 0.05) / (bg + 0.05);
    const onDark = (bg + 0.05) / (luminance('#0F172A') + 0.05);
    return onWhite >= onDark ? '#FFFFFF' : '#0F172A';
}

/**
 * Every --rw-* variable for a brand. The derived shades use the same lightness steps RealWorld's
 * own palette has (e.g. #D4A865 is #B08A52 about 25% toward white).
 * @param {ICoBrandTheme} theme brand colors
 * @returns {Record<string, string>} CSS variable -> value
 */
function brandVariables(theme: ICoBrandTheme): Record<string, string> {
    const vars: Record<string, string> = {};
    Object.entries(BASE_VARIABLES).forEach(([name, key]) => (vars[name] = theme[key]));
    const isHex = (value?: string): value is string => !!value && /^#[0-9A-Fa-f]{6}$/.test(value);
    vars['--rw-text'] = isHex(theme.text) ? theme.text : '#162642';
    if (isHex(theme.header_bg)) {
        vars['--rw-header-bg'] = theme.header_bg;
        vars['--rw-header-btn'] = tint(theme.header_bg, 0.15);
    }
    if (isHex(theme.footer_bg)) {
        vars['--rw-footer-bg'] = theme.footer_bg;
        // dark footer: white text; light footer keeps RealWorld's dark footer text
        const darkFooter = (1 + 0.05) / (luminance(theme.footer_bg) + 0.05) >= 4.5;
        if (darkFooter) {
            vars['--rw-footer-text'] = 'rgba(255, 255, 255, 0.85)';
            vars['--rw-footer-link'] = '#FFFFFF';
        }
    }
    // cards and the sidebar: the page color lifted most of the way to white, so they sit on the
    // page in the brand's own hue instead of plain white; a dark page keeps RealWorld's white
    if (isHex(theme.page_bg) && luminance(theme.page_bg) > 0.5) {
        // stripes, hover and shimmer need a tone darker than the cards; a near-white page would
        // make them vanish, so it is stepped down first (#FFFFFF gives RealWorld's #F0F0F0)
        const tone = luminance(theme.page_bg) > 0.9 ? shade(theme.page_bg, 0.06) : theme.page_bg;
        vars['--rw-surface'] = tint(theme.page_bg, 0.6);
        vars['--rw-surface-hover'] = shade(tone, 0.04);
        // inner cards and table heads/stripes step down from the surface toward the tone
        vars['--rw-surface-alt'] = tint(tone, 0.4);
        vars['--rw-surface-head'] = tint(tone, 0.4);
        vars['--rw-surface-soft'] = tint(tone, 0.5);
        vars['--rw-row-alt'] = tint(tone, 0.3);
        vars['--rw-row-hover'] = tone;
        // shimmer loaders: the tone for the blocks, a little darker for the moving shine
        vars['--rw-skeleton'] = tone;
        vars['--rw-skeleton-shine'] = shade(tone, 0.06);
        vars['--rw-skeleton-cool'] = tone;
        vars['--rw-skeleton-cool-shine'] = shade(tone, 0.05);
        vars['--rw-skeleton-block'] = shade(tone, 0.02);
        // tab and filter pills: a step darker than the page, so they read as buttons on it
        vars['--rw-tab'] = shade(tone, 0.06);
        vars['--rw-tab-hover'] = shade(tone, 0.09);
        vars['--rw-tab-soft'] = shade(tone, 0.03);
        vars['--rw-tab-strong'] = shade(tone, 0.1);
        vars['--rw-tab-line'] = shade(tone, 0.12);
    }
    const authText = authTextColor(theme);
    vars['--rw-auth-text'] = authText;
    // secondary text on the log-in pages: the text color blended toward the background
    vars['--rw-auth-muted'] = blend(authText, theme.auth_bg, 0.3);
    vars['--rw-auth-muted-2'] = blend(authText, theme.auth_bg, 0.45);
    vars['--rw-accent-mid'] = tint(theme.accent, 0.25);
    vars['--rw-accent-light'] = tint(theme.accent, 0.6);
    vars['--rw-auth-surface'] = tint(theme.auth_bg, 0.07);
    vars['--rw-auth-input'] = tint(theme.auth_bg, 0.1);
    vars['--rw-auth-deep'] = tint(theme.auth_bg, 0.05);
    vars['--rw-auth-raised'] = tint(theme.auth_bg, 0.14);
    vars['--rw-auth-raised-2'] = tint(theme.auth_bg, 0.15);
    vars['--rw-auth-line'] = tint(theme.auth_bg, 0.11);
    // header buttons (bell, profile, Log in), deposit badges, darker accents
    vars['--rw-primary-soft'] = tint(theme.primary, 0.15);
    vars['--rw-primary-soft-2'] = tint(theme.primary, 0.08);
    vars['--rw-primary-deep'] = shade(theme.primary, 0.35);
    vars['--rw-header-btn-border'] = 'rgba(255, 255, 255, 0.45)';
    vars['--rw-register-from'] = tint(theme.auth_bg, 0.12);
    vars['--rw-register-to'] = theme.auth_bg;
    return vars;
}

/**
 * The community number in a URL query string.
 * @param {string} search query string, e.g. window.location.search
 * @returns {number | null} the number, or null when missing or not a number (?community_id=0 included)
 */
export function resolveCommunityId(search: string): number | null {
    const value = (new URLSearchParams(search).get(COMMUNITY_PARAM) || '').trim();
    return COMMUNITY_ID_PATTERN.test(value) ? Number(value) : null;
}

/**
 * Community number for this page load: from its own URL, or, for a page running inside one of our
 * own frames without one (iDenfy sends KYC back to /kyc-verify inside the /kyc iframe), from the
 * page around it.
 * @returns {number | null} the number, or null for RealWorld
 */
function communityIdFromUrl(): number | null {
    const own = resolveCommunityId(window.location.search);
    if (own || window.parent === window) return own;
    try {
        return resolveCommunityId(window.parent.location.search);
    } catch {
        // a frame of another site: its URL cannot be read
        return null;
    }
}

/**
 * Splits a URL into the part before the query, the query and the hash.
 * @param {string} url URL or path
 * @returns {[string, URLSearchParams, string]} path, query params, hash (with its '#')
 */
function splitUrl(url: string): [string, URLSearchParams, string] {
    const hashAt = url.indexOf('#');
    const hash = hashAt >= 0 ? url.slice(hashAt) : '';
    const rest = hashAt >= 0 ? url.slice(0, hashAt) : url;
    const queryAt = rest.indexOf('?');
    if (queryAt < 0) return [rest, new URLSearchParams(), hash];
    return [rest.slice(0, queryAt), new URLSearchParams(rest.slice(queryAt + 1)), hash];
}

/**
 * Adds the active community's ?community_id= to an app URL ('/lend', 'lend?tab=1'), so a page
 * opened from here (new tab, full page load) keeps the community. Outside a community, and for
 * other sites' URLs, the URL is returned unchanged.
 * @param {string} url app URL
 * @returns {string} the URL with the community number
 */
export function withCommunity(url: string): string {
    if (!activeCommunityId || /^([a-z][a-z0-9+.-]*:|\/\/)/i.test(url)) return url;
    const [path, params, hash] = splitUrl(url);
    params.set(COMMUNITY_PARAM, String(activeCommunityId));
    return `${path}?${params.toString()}${hash}`;
}

/**
 * Removes ?community_id= from a URL, so the router never sees it.
 * @param {string} url app URL as the browser shows it
 * @returns {string} the URL without the community number
 */
export function withoutCommunity(url: string): string {
    const [path, params, hash] = splitUrl(url);
    if (!params.has(COMMUNITY_PARAM)) return url;
    params.delete(COMMUNITY_PARAM);
    const query = params.toString();
    return `${path}${query ? `?${query}` : ''}${hash}`;
}

/**
 * The Community-Id header, added to every API call by RegulatedInterceptor while a signed-in member
 * is in their community's view. Empty for a visitor who is not signed in (the API answers a header
 * without a sign-in with 401, sign-in and sign-up included), on RealWorld, and for an unknown or
 * disabled community. A visitor's sign-up still joins the community through the community_id
 * in the sign-up body (AccountService).
 * @returns {Record<string, string>} headers to add to the request
 */
export function communityHeaders(): Record<string, string> {
    return activeCommunityId && signedInMember ? { [COMMUNITY_HEADER]: String(activeCommunityId) } : {};
}

/**
 * Signed out: the page keeps the community's look for the visitor, and its calls stop carrying
 * the Community-Id header. Called by UserStateService.setUser(null).
 * @returns {void}
 */
export function forgetSignedInMember(): void {
    signedInMember = false;
}

/**
 * Number of the community this page shows, sent with the sign-up requests so the new account
 * joins it.
 * @returns {number | null} the number, or null on RealWorld
 */
export function getActiveCommunityId(): number | null {
    return activeCommunityId;
}

/**
 * Keeps ?community_id= in the address bar on every page while a community is shown, without the
 * router ever seeing it: router URLs, routerLink active checks and code comparing router.url stay
 * exactly as on RealWorld. Link hrefs get the number too, so "open in new tab" keeps the community.
 * Provided as the LocationStrategy in app.module.ts.
 */
@Injectable()
export class CommunityLocationStrategy extends PathLocationStrategy {
    override path(includeHash = false): string {
        return withoutCommunity(super.path(includeHash));
    }

    override prepareExternalUrl(internal: string): string {
        return super.prepareExternalUrl(withCommunity(internal));
    }
}

/**
 * Fetches the brand for a community number from the public API.
 * @param {number} communityId community number
 * @returns {Promise<ICoBrand | null | undefined>} the brand (active or disabled), null when unknown or deleted, undefined on network error
 */
async function fetchBrand(communityId: number): Promise<ICoBrand | null | undefined> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
        const res = await fetch(`${environment.API_BASE_URL_V3}/community`, {
            headers: { [COMMUNITY_HEADER]: String(communityId) },
            signal: controller.signal,
            credentials: 'omit',
        });
        if (res.status === 404) return null;
        if (!res.ok) return undefined;
        const body = await res.json();
        return (body?.data as ICoBrand) || null;
    } catch {
        return undefined;
    } finally {
        clearTimeout(timer);
    }
}

/**
 * The signed-in user's community from GET /auth/me, which returns it with the same public fields
 * as GET /community (or null for a user with no community or a deleted one).
 * @returns {Promise<ICoBrand | null | undefined>} the community, or null, for a signed-in user; undefined when nobody is signed in or the API did not answer
 */
async function fetchSessionCommunity(): Promise<ICoBrand | null | undefined> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
        const res = await fetch(`${environment.API_BASE_URL_V3}/auth/me`, {
            signal: controller.signal,
            credentials: 'include',
        });
        if (!res.ok) return undefined;
        const community = (await res.json())?.data?.community_id;
        return community && typeof community === 'object' ? (community as ICoBrand) : null;
    } catch {
        return undefined;
    } finally {
        clearTimeout(timer);
    }
}

let titleName: string | null = null;
let titleWatched = false;

/**
 * Keeps the browser tab title on the brand's name, including titles set later by pages and
 * name changes made in the admin panel's live preview. One observer for the page's lifetime.
 * @param {string} name brand name
 * @returns {void}
 */
function brandTitle(name: string): void {
    const previous = titleName;
    titleName = name;
    const swap = () => {
        if (!titleName) return;
        let next = document.title.replace(/RealWorld\.fi|RealWorld/g, titleName);
        if (previous && previous !== titleName) next = next.split(previous).join(titleName);
        if (next !== document.title) document.title = next;
    };
    swap();
    const titleEl = document.querySelector('title');
    if (titleEl && !titleWatched) {
        titleWatched = true;
        new MutationObserver(swap).observe(titleEl, { childList: true, characterData: true, subtree: true });
    }
}

/**
 * Applies a brand to the page, or restores the RealWorld look when brand is null.
 * @param {ICoBrand | null} brand brand to apply
 * @returns {void}
 */
function applyBrand(brand: ICoBrand | null): void {
    const root = document.documentElement;
    THEME_VARIABLE_NAMES.forEach((name) => root.style.removeProperty(name));
    activeBrand = brand;
    document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]').forEach((link) => {
        if (!originalFavicons.has(link)) originalFavicons.set(link, link.href);
        link.href = brand?.favicon_url || originalFavicons.get(link) || link.href;
    });
    if (!brand) {
        // a brand was shown and is gone now (sign-in switched to RealWorld): give the tab title back
        if (titleName) document.title = document.title.split(titleName).join('RealWorld.fi');
        titleName = null;
        return;
    }
    Object.entries(brandVariables(brand.theme)).forEach(([name, value]) => root.style.setProperty(name, value));
    brandTitle(brand.name);
}

/**
 * Shows a community (or RealWorld for null) on the running page: theme, logo, the Community-Id
 * header and the number in the address bar. Later navigations keep it through CommunityLocationStrategy.
 * @param {ICoBrand | null} brand the active community, or null for RealWorld
 * @returns {void}
 */
function showCommunity(brand: ICoBrand | null): void {
    activeCommunityId = brand?.community_id ?? null;
    // only called for a signed-in user's own community (or RealWorld)
    signedInMember = activeCommunityId != null;
    applyBrand(brand);
    const url = withoutCommunity(window.location.pathname + window.location.search + window.location.hash);
    history.replaceState(history.state, '', withCommunity(url));
}

let followRun = 0;

/**
 * A signed-in user's community: the number in the sign-in responses, the community itself in GET /auth/me.
 * @param {Record<string, unknown> | null | undefined} user the signed-in user
 * @returns {{ id: number | null, community: ICoBrand | null }} the number (null for none), and the community when known
 */
function userCommunity(user: Record<string, unknown> | null | undefined): { id: number | null; community: ICoBrand | null } {
    const value = user?.['community_id'];
    const community = value && typeof value === 'object' ? (value as ICoBrand) : null;
    const id = community ? community.community_id : typeof value === 'number' && value > 0 ? value : null;
    return { id, community };
}

/**
 * After a sign-in on a page opened with ?community_id=: the signed-in user's community decides the
 * view, as at startup. Their community's number replaces the one in the URL; no community (null),
 * or a disabled or deleted one, gives the RealWorld site. Called by UserStateService.setUser.
 * @param {Record<string, unknown>} user the signed-in user: community_id is the number in the sign-in responses, the community itself in GET /auth/me
 * @returns {Promise<void>}
 */
export async function followSignedInCommunity(user: Record<string, unknown>): Promise<void> {
    if (!openedWithCommunity) return;
    const { id, community } = userCommunity(user);
    if (id === activeCommunityId) {
        // a member of the community already shown (a visitor who signed in, or the startup user)
        signedInMember = id != null;
        return;
    }

    const run = ++followRun;
    const brand = community || (id ? await fetchBrand(id) : null);
    // a newer sign-in (or sign-out and sign-in) already decided
    if (run !== followRun) return;
    showCommunity(brand?.status === 'active' ? brand : null);
}

const PREVIEW_PARAM = 'community_preview';
const PREVIEW_MESSAGE = 'rw:community-preview';
const HEX = /^#[0-9A-Fa-f]{6}$/;

/**
 * Whether a postMessage sender may drive the preview: the admin panel's own hosts, plus
 * localhost outside production builds (local development).
 * @param {string} origin message origin
 * @returns {boolean} true when trusted
 */
function isPreviewOrigin(origin: string): boolean {
    try {
        const { protocol, hostname } = new URL(origin);
        if (protocol === 'https:' && /^(dev|staging|pre)?admin\.realworld\.fi$/.test(hostname)) return true;
        return !environment.production && (hostname === 'localhost' || hostname === '127.0.0.1');
    } catch {
        return false;
    }
}

/**
 * Checks the shape of a brand sent by the admin panel's preview before it touches the page.
 * @param {unknown} value message payload
 * @returns {ICoBrand | null} the brand, or null when malformed
 */
function toPreviewBrand(value: unknown): ICoBrand | null {
    const b = value as Partial<ICoBrand> | null;
    const t = b?.theme as Partial<ICoBrandTheme> | undefined;
    if (!b || typeof b.name !== 'string' || !t) return null;
    if (![t.primary, t.secondary, t.accent, t.page_bg, t.auth_bg].every((c) => typeof c === 'string' && HEX.test(c))) return null;
    const url = (u: unknown) => (typeof u === 'string' && /^(https:|data:image\/)/.test(u) ? u : '');
    return {
        name: b.name.slice(0, 60) || 'Brand name',
        community_id: typeof b.community_id === 'number' ? b.community_id : null,
        theme: t as ICoBrandTheme,
        logo_url: url(b.logo_url),
        favicon_url: url(b.favicon_url),
        footer_text: typeof b.footer_text === 'string' ? b.footer_text.slice(0, 160) : '',
        privacy_policy_url: url(b.privacy_policy_url),
    };
}

/**
 * Preview mode (?community_preview=1): the admin panel's Community Management page shows the real marketplace in an
 * iframe and sends the unsaved brand by postMessage on every change. Nothing is fetched, cached
 * or saved; the page just repaints with the received brand.
 * @returns {void}
 */
let previewActive = false;

/** The admin page (iframe parent) or the window that opened this one, in preview mode. */
function previewHost(): Window | null {
    return window.parent !== window ? window.parent : window.opener;
}

function startPreview(): void {
    previewActive = true;
    window.addEventListener('message', (event: MessageEvent) => {
        if (!isPreviewOrigin(event.origin) || event.data?.type !== PREVIEW_MESSAGE) return;
        const brand = toPreviewBrand(event.data.brand);
        if (brand) applyBrand(brand);
    });
    // tell the admin panel the page is ready to receive the brand (sent before the app has drawn anything)
    previewHost()?.postMessage({ type: `${PREVIEW_MESSAGE}:ready` }, '*');
}

/**
 * In preview mode, tells the admin panel the marketplace has drawn its first screen, so it can drop its
 * "loading" cover. Call after bootstrap; does nothing outside preview mode.
 * @returns {void}
 */
export function notifyPreviewRendered(): void {
    if (previewActive) previewHost()?.postMessage({ type: `${PREVIEW_MESSAGE}:rendered` }, '*');
}

/**
 * Detects and applies the community for this page load. Call once in main.ts before bootstrap.
 * Never throws: on any failure the marketplace keeps the RealWorld look.
 * @returns {Promise<ICoBrandBoot>} the brand (or null), and whether the app must not start
 */
export async function loadCoBrand(): Promise<ICoBrandBoot> {
    try {
        OLD_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
    } catch {
        /* storage blocked: nothing was stored either */
    }
    if (new URLSearchParams(window.location.search).has(PREVIEW_PARAM) && (window.parent !== window || window.opener)) {
        startPreview();
        return { brand: null };
    }
    const id = communityIdFromUrl();
    if (!id) return { brand: null };
    openedWithCommunity = true;

    // both start together; the signed-in check decides first
    const brandRequest = fetchBrand(id);
    const member = await fetchSessionCommunity();
    if (member !== undefined) {
        // signed in: the community /auth/me gives them, whatever number the URL has; null (or a
        // disabled one) gives RealWorld
        const brand = member?.status === 'active' ? member : null;
        showCommunity(brand);
        return { brand };
    }

    // not signed in (or /auth/me did not answer): the community's look, for its sign-up link
    const fetched = await brandRequest;
    // unknown or deleted community: the normal RealWorld site
    if (fetched === null) return { brand: null };
    if (fetched?.status === 'disabled') {
        applyBrand(fetched);
        return { brand: fetched, unavailable: 'disabled' };
    }
    // the API did not answer: do not guess the community's look
    if (fetched === undefined) return { brand: null, unavailable: 'offline' };
    activeCommunityId = id;
    signedInMember = false;
    applyBrand(fetched);
    return { brand: fetched };
}

/**
 * Small element factory for the unavailable page; text goes through textContent only.
 * @param {string} tag element name
 * @param {string} className CSS class
 * @param {string} text text content
 * @returns {HTMLElement} the element
 */
function el(tag: string, className: string, text = ''): HTMLElement {
    const node = document.createElement(tag);
    node.className = className;
    if (text) node.textContent = text;
    return node;
}

/**
 * Replaces the app with a branded "temporarily unavailable" page. Used instead of starting the
 * marketplace when a brand is disabled, or when its details cannot be loaded, so the partner's
 * users never see "page not found" for a valid address.
 * @param {ICoBrandBoot} boot result of loadCoBrand()
 * @returns {void}
 */
export function renderCoBrandUnavailable(boot: ICoBrandBoot): void {
    const brand = boot.brand;
    const name = brand?.name || 'This page';
    const offline = boot.unavailable === 'offline';
    document.title = brand ? `${brand.name} - Temporarily unavailable` : 'Temporarily unavailable';

    const style = document.createElement('style');
    style.textContent = `
.rw-down { min-height: 100vh; display: flex; flex-direction: column; background: var(--rw-page-bg); font-family: "Open Sans", Arial, sans-serif; color: #1b2233; }
.rw-down-header { background: var(--rw-primary); padding: 14px 24px; display: flex; align-items: center; min-height: 64px; }
.rw-down-header img { max-height: 40px; max-width: 220px; object-fit: contain; }
.rw-down-header .rw-down-name { color: #fff; font-weight: 700; font-size: 20px; }
.rw-down-main { flex: 1; display: flex; align-items: center; justify-content: center; padding: 32px 16px; }
.rw-down-card { background: #fff; border-radius: 16px; padding: 40px 32px; max-width: 480px; width: 100%; text-align: center; box-shadow: 0 8px 30px rgba(0,0,0,.06); }
.rw-down-icon { width: 64px; height: 64px; border-radius: 50%; margin: 0 auto 20px; display: flex; align-items: center; justify-content: center; background: color-mix(in srgb, var(--rw-primary) 10%, transparent); color: var(--rw-primary); }
.rw-down-card h1 { font-size: 24px; margin: 0 0 10px; color: var(--rw-primary); }
.rw-down-card p { font-size: 15px; line-height: 1.6; color: #4b5563; margin: 0 0 8px; }
.rw-down-card a { color: var(--rw-primary); font-weight: 600; }
.rw-down-retry { margin-top: 18px; border: 0; border-radius: 8px; padding: 10px 22px; font-size: 15px; font-weight: 600; cursor: pointer; background: var(--rw-primary); color: #fff; }
.rw-down-footer { text-align: center; padding: 16px; font-size: 12px; color: #66788a; }
.rw-down-footer b { font-weight: 600; }`;
    document.head.appendChild(style);

    const page = el('div', 'rw-down');
    const header = el('header', 'rw-down-header');
    if (brand?.logo_url) {
        const logo = document.createElement('img');
        logo.src = brand.logo_url;
        logo.alt = brand.name;
        header.appendChild(logo);
    } else {
        header.appendChild(el('span', 'rw-down-name', brand?.name || ''));
    }

    const card = el('div', 'rw-down-card');
    const icon = el('div', 'rw-down-icon');
    icon.innerHTML = '<svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>';
    card.appendChild(icon);
    card.appendChild(el('h1', '', offline ? 'Temporarily unavailable' : "We'll be back soon"));
    card.appendChild(el('p', '', offline
        ? 'We could not load this page right now. Please check your connection and try again.'
        : `${name} is temporarily unavailable while we make some updates. Please check back later.`));
    const retry = el('button', 'rw-down-retry', 'Try again') as HTMLButtonElement;
    retry.type = 'button';
    retry.addEventListener('click', () => window.location.reload());
    card.appendChild(retry);

    const main = el('main', 'rw-down-main');
    main.appendChild(card);
    const footer = el('footer', 'rw-down-footer', 'Powered by ');
    footer.appendChild(el('b', '', 'RealWorld.fi'));
    page.append(header, main, footer);

    const root = document.querySelector('app-root');
    if (root) root.replaceWith(page);
    else document.body.prepend(page);
}

/**
 * Current value of a brand color variable, for code that needs a real color string (charts).
 * @param {string} variable CSS variable, e.g. '--rw-primary'
 * @param {string} fallback RealWorld value
 * @returns {string} the color
 */
export function brandColor(variable: string, fallback: string): string {
    return getComputedStyle(document.documentElement).getPropertyValue(variable).trim() || fallback;
}

/** Read access to the active co-brand for components (logo, name, footer line). */
@Injectable({ providedIn: 'root' })
export class CoBrandService {
    /** The active brand, or null on RealWorld.fi. */
    get brand(): ICoBrand | null {
        return activeBrand;
    }

    /** Display name: the brand's, or RealWorld.fi. */
    get name(): string {
        return activeBrand?.name || 'RealWorld.fi';
    }

    /**
     * Logo for the dark header and sign-in pages.
     * @param {string} fallback RealWorld logo path
     * @returns {string} logo URL
     */
    logo(fallback: string): string {
        return activeBrand?.logo_url || fallback;
    }

    /**
     * Logo for the log-in / register pages: on a light sign-in background RealWorld uses its
     * light-background logo; a brand has one logo, used on both.
     * @returns {string} logo URL
     */
    authLogo(): string {
        const text = activeBrand ? authTextColor(activeBrand.theme) : '#FFFFFF';
        const lightBackground = parseInt(text.slice(1, 3), 16) + parseInt(text.slice(3, 5), 16) + parseInt(text.slice(5, 7), 16) < 384;
        return lightBackground ? this.logoDark('assets/images/logo-light.svg') : this.logo('assets/images/logo-fi.svg');
    }

    /**
     * Logo for light backgrounds: the brand's one logo, or RealWorld's light-background logo.
     * @param {string} fallback RealWorld logo path
     * @returns {string} logo URL
     */
    logoDark(fallback: string): string {
        return activeBrand?.logo_url || fallback;
    }

    /**
     * Footer company line.
     * @param {string} fallback RealWorld's line
     * @returns {string} footer text
     */
    footerText(fallback: string): string {
        return activeBrand ? activeBrand.footer_text || activeBrand.name : fallback;
    }

    /** Whether a signed-in member is in their community's view (Community-Id is sent). */
    get memberView(): boolean {
        return !!activeBrand && signedInMember;
    }

    /**
     * The first of these items held at a location locked to the community this page shows, from
     * the community's `locations`. Names are trimmed and compared without case, as the backend does.
     * @param {Array<{ attributes?: unknown }> | null | undefined} nfts the items of a listing or loan request
     * @returns {string | null} the item's location name, or null when none is locked (or no location is)
     */
    lockedLocation(nfts: Array<{ attributes?: unknown } | null | undefined> | null | undefined): string | null {
        const locked = (activeBrand?.locations || []).map((name) => String(name).trim().toLowerCase()).filter(Boolean);
        if (!locked.length) return null;
        for (const nft of nfts || []) {
            const attributes = Array.isArray(nft?.attributes) ? (nft.attributes as Array<{ key?: string; value?: unknown }>) : [];
            const name = String(attributes.find((attribute) => LOCATION_KEYS.includes(String(attribute?.key)))?.value || '').trim();
            if (name && locked.includes(name.toLowerCase())) return name;
        }
        return null;
    }

    /**
     * Name of a deal's community, for the members-only notes: the community this page shows, or the
     * signed-in user's own community, when the deal belongs to it.
     * @param {number | null | undefined} communityId the deal's community_id
     * @param {Record<string, unknown> | null | undefined} user the signed-in user
     * @returns {string | null} the community's name, or null when it is not known here
     */
    /**
     * Whether the signed-in user belongs to a community, e.g. the community of a members-only listing.
     * @param {number | null | undefined} communityId the community's number; none gives false
     * @param {Record<string, unknown> | null | undefined} user the signed-in user
     * @returns {boolean}
     */
    isUserInCommunity(communityId: number | null | undefined, user: Record<string, unknown> | null | undefined): boolean {
        return communityId != null && userCommunity(user).id === Number(communityId);
    }

    communityName(communityId: number | null | undefined, user: Record<string, unknown> | null | undefined): string | null {
        if (communityId == null) return null;
        if (activeBrand?.community_id === Number(communityId)) return activeBrand.name;
        const { id, community } = userCommunity(user);
        return id === Number(communityId) && community?.name ? community.name : null;
    }

    /**
     * The members_only value to send for a new sale listing or loan request, in a member's
     * community view:
     * - an item at a location locked to the community: always true, whatever the tick (the
     *   checkbox shows ticked and cannot be changed; the backend locks the deal the same way);
     * - an item at an open location: the member's tick.
     * Outside a member's community view: false.
     * @param {boolean} ticked the checkbox value
     * @param {Array<{ attributes?: unknown }> | null | undefined} nfts the items of the deal
     * @returns {boolean} true to send members_only: true
     */
    membersOnlyChoice(ticked: boolean, nfts: Array<{ attributes?: unknown } | null | undefined> | null | undefined): boolean {
        if (!this.memberView) return false;
        return !!this.lockedLocation(nfts) || !!ticked;
    }

    /**
     * The community a signed-in member can switch to from the profile menu: their own community
     * while the page shows the RealWorld site. Null for a user with no community, a disabled one,
     * or when the page already shows it.
     * @param {Record<string, unknown> | null | undefined} user the signed-in user
     * @returns {{ community_id: number, name: string } | null} the community to offer
     */
    switchTarget(user: Record<string, unknown> | null | undefined): { community_id: number; name: string } | null {
        const { id, community } = userCommunity(user);
        if (!id || id === activeCommunityId || community?.status === 'disabled') return null;
        return { community_id: id, name: community?.name || 'your community' };
    }

    /**
     * Opens the current page in a community's view. The page loads again, so the community's look
     * is in place before anything is drawn, the same as opening a community link.
     * @param {number} communityId the community's number
     * @returns {void}
     */
    openCommunity(communityId: number): void {
        const [path, params, hash] = splitUrl(window.location.pathname + window.location.search + window.location.hash);
        params.set(COMMUNITY_PARAM, String(communityId));
        window.location.assign(`${path}?${params.toString()}${hash}`);
    }
}
